<script setup lang="ts">
import { ref, watch } from 'vue';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import type { Entity } from '@/features/Entity_my/types/entity.types';
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';

const props = defineProps<{
  active?: boolean;
}>();

const emit = defineEmits<{
  (e: 'prepare', entityCd: string): void;
}>();

const searchQuery = ref('');
const debouncedSearch = ref('');
const entities = ref<Entity[]>([]);
const loading = ref(false);
const loadError = ref<string | null>(null);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

type DateStatus = 'pastDue' | 'thisMonth' | 'nextMonth' | 'later' | null;

function getDateStatus(dateStr: string | null | undefined): DateStatus {
  if (!dateStr) return null;

  const today = new Date();
  const targetDate = new Date(dateStr);
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const targetMonth = targetDate.getMonth();
  const targetYear = targetDate.getFullYear();

  if (targetDate < today) return 'pastDue';
  if (targetYear === currentYear && targetMonth === currentMonth) return 'thisMonth';

  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;
  if (targetYear === nextMonthYear && targetMonth === nextMonth) return 'nextMonth';

  return 'later';
}

function dueBadge(status: DateStatus): { label: string; color: string } | null {
  if (status === 'pastDue') return { label: 'Past Due', color: 'error' };
  if (status === 'thisMonth') return { label: 'This Month', color: 'warning' };
  if (status === 'nextMonth') return { label: 'Next Month', color: 'warning' };
  return null;
}

async function loadEntities() {
  loading.value = true;
  loadError.value = null;
  try {
    const search = debouncedSearch.value.trim();
    const res = await entityMyApi.getEntities({
      page: currentPage.value,
      limit: itemsPerPage.value,
      onlyLive: true,
      ...(search ? { search } : {}),
    });
    entities.value = res.data?.entities || [];
    total.value = res.data?.total ?? entities.value.length;
  } catch (err: any) {
    loadError.value = err?.message || 'Failed to load entities';
    entities.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

function rowNumber(index: number): number {
  return (currentPage.value - 1) * itemsPerPage.value + index + 1;
}

watch(searchQuery, (value) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    debouncedSearch.value = value;
    currentPage.value = 1;
  }, 300);
});

watch(
  [() => props.active, debouncedSearch, currentPage, itemsPerPage],
  ([active]) => {
    if (active) void loadEntities();
  },
  { immediate: true },
);

watch(
  () => props.active,
  (active) => {
    if (!active) return;
    searchQuery.value = '';
    debouncedSearch.value = '';
    currentPage.value = 1;
  },
);

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return formatDateByJurisdiction(dateStr) || '-';
}

function registrationNo(entity: Entity): string {
  return String(entity.registrationNumber || entity.regNo || '-');
}

function arDueDisplay(entity: Entity) {
  const status = getDateStatus(entity.nextARDueDate);
  return {
    date: formatDate(entity.nextARDueDate),
    badge: dueBadge(status),
  };
}

function handlePrepare(entity: Entity) {
  const entityCd = String(entity.id || '').trim();
  if (!entityCd) return;
  emit('prepare', entityCd);
}
</script>

<template>
  <div class="prepare-ar-panel">
    <div class="prepare-ar-toolbar">
      <v-text-field
        v-model="searchQuery"
        density="compact"
        variant="outlined"
        placeholder="Search entity…"
        prepend-inner-icon="mdi-magnify"
        hide-details
        rounded="lg"
        class="prepare-ar-search"
      />
    </div>

    <div v-if="loading" class="prepare-ar-state">
      <v-progress-circular indeterminate size="28" width="3" />
    </div>

    <div v-else-if="loadError" class="prepare-ar-state text-medium-emphasis">
      {{ loadError }}
    </div>

    <template v-else>
      <div class="prepare-ar-table-wrap">
        <v-table density="comfortable" class="prepare-ar-table">
          <thead>
            <tr>
              <th class="prepare-ar-no-col">#</th>
              <th>Entity</th>
              <th>Registration No.</th>
              <th>Next AR Due</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-if="entities.length === 0">
              <td colspan="5" class="text-center text-medium-emphasis py-6">
                No entities found
              </td>
            </tr>
            <tr v-for="(entity, index) in entities" :key="entity.id">
              <td class="text-medium-emphasis prepare-ar-no-col">{{ rowNumber(index) }}</td>
              <td class="font-weight-medium">{{ entity.name }}</td>
              <td>{{ registrationNo(entity) }}</td>
              <td>
                {{ arDueDisplay(entity).date }}
                <v-chip
                  v-if="arDueDisplay(entity).badge"
                  size="x-small"
                  :color="arDueDisplay(entity).badge!.color"
                  variant="tonal"
                  label
                  class="ms-1"
                >
                  {{ arDueDisplay(entity).badge!.label }}
                </v-chip>
              </td>
              <td class="text-right">
                <v-btn
                  size="small"
                  color="primary"
                  variant="flat"
                  class="text-none"
                  @click="handlePrepare(entity)"
                >
                  Prepare
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <div class="prepare-ar-footer">
        <span class="prepare-ar-total text-caption text-medium-emphasis">
          {{ total }} {{ total === 1 ? 'entity' : 'entities' }}
        </span>
        <div v-if="total > itemsPerPage" class="prepare-ar-pagination">
          <v-pagination
            v-model="currentPage"
            :length="Math.ceil(total / itemsPerPage)"
            :total-visible="7"
            density="compact"
            rounded="circle"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.prepare-ar-panel {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
}

.prepare-ar-toolbar {
  flex: 0 0 auto;
  margin-bottom: 12px;
}

.prepare-ar-search {
  max-width: 320px;
  width: 320px;
}

.prepare-ar-search :deep(.v-input) {
  flex: none;
}

.prepare-ar-search :deep(.v-field) {
  min-height: 40px;
  height: 40px;
}

.prepare-ar-state {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prepare-ar-table-wrap {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}

.prepare-ar-no-col {
  width: 48px;
  text-align: center;
  white-space: nowrap;
}

.prepare-ar-table :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 12px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSecondary));
  white-space: nowrap;
  background: rgb(var(--v-theme-surface));
}

.prepare-ar-table :deep(tbody td) {
  vertical-align: middle;
}

.prepare-ar-footer {
  flex-shrink: 0;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  margin-top: 4px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.prepare-ar-total {
  align-self: flex-start;
}

.prepare-ar-pagination {
  display: flex;
  justify-content: center;
  width: 100%;
}

.prepare-ar-pagination :deep(.v-pagination) {
  justify-content: center;
}
</style>
